import Box from '@mui/material/Box';
import type { Theme } from '@mui/material/styles';
import { keyframes } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Initial stagger time of 5 seconds, before we get any thinking message from the server
const THINKING_LINE_STAGGER_INITIAL_MS = 5000;
// After we get the first thinking message from the server, we stagger by 1 second per line
const THINKING_LINE_STAGGER_MS = 1000;

/** Shared shimmer used by the thinking line and condensed thinking-process previews. */
const thinkingShimmer = keyframes({
  to: { backgroundPosition: '200% center' },
});

const thinkingLineReveal = keyframes({
  from: {
    opacity: 0,
    transform: 'translateY(10px)',
  },
  to: {
    opacity: 1,
    transform: 'translateY(0)',
  },
});

const thinkingRowTypographySx = (theme: Theme) => ({
  m: 0,
  fontSize: 15,
  fontWeight: 'fontWeightBold',
  wordBreak: 'break-word' as const,
  background: `linear-gradient(90deg,${theme.palette.text.disabled},${theme.palette.text.secondary},${theme.palette.text.disabled})`,
  backgroundSize: '200% auto',
  animation: `${thinkingShimmer} 3s linear infinite`,
  backgroundClip: 'text' as const,
  color: 'transparent',
});

function getUpperBound(id?: string): number {
  let upperBound = 1;
  if (!id) {
    return upperBound;
  }
  const linesFromStorage = sessionStorage.getItem(`openx-thinking-${id}`);
  if (linesFromStorage && Number.isInteger(Number(linesFromStorage))) {
    upperBound = Number(linesFromStorage);
  }
  return upperBound;
}

export type ChatThinkingContentProps = {
  id?: string;
  thinkingPlaceholderMessages?: readonly string[];
  thinkingProcessMessages?: readonly string[];
};

export function ChatThinkingContent({
  id,
  thinkingPlaceholderMessages,
  thinkingProcessMessages = [],
}: ChatThinkingContentProps) {
  const { t } = useTranslation();
  const upperBoundRef = useRef(getUpperBound(id));

  const [placeholderMessages] = useState<readonly string[]>(() => {
    if (!thinkingPlaceholderMessages) {
      return [
        t('Analyzing the request'),
        t('Planning next steps'),
        t('Selecting the right tools'),
        t('Constructing queries'),
        t('Executing plan'),
      ];
    }

    return thinkingPlaceholderMessages;
  });

  const lines = useMemo(() => {
    return [...placeholderMessages, ...thinkingProcessMessages];
  }, [placeholderMessages, thinkingProcessMessages]);

  const [linesToDisplay, setLinesToDisplay] = useState<readonly string[]>(() => {
    const upperBound = Math.min(upperBoundRef.current, lines.length);
    return lines.slice(Math.max(0, upperBoundRef.current - 3), upperBound);
  });

  useEffect(() => {
    if (!id) {
      return;
    }

    if (upperBoundRef.current >= lines.length) {
      return;
    }

    const staggerMs =
      lines.length > placeholderMessages.length ? THINKING_LINE_STAGGER_MS : THINKING_LINE_STAGGER_INITIAL_MS;

    const intervalId = window.setInterval(() => {
      if (upperBoundRef.current >= lines.length) {
        window.clearInterval(intervalId);
        return;
      }

      upperBoundRef.current = Math.min(lines.length, upperBoundRef.current + 1);
      sessionStorage.setItem(`openx-thinking-${id}`, upperBoundRef.current.toString());
      setLinesToDisplay(() => {
        const upperBound = Math.min(upperBoundRef.current, lines.length);
        return lines.slice(Math.max(0, upperBoundRef.current - 3), upperBound);
      });
    }, staggerMs);

    return () => window.clearInterval(intervalId);
  }, [id, lines, placeholderMessages.length]);

  return (
    <Box
      sx={theme => ({
        overflow: 'hidden',
        lineHeight: theme.typography.body2.lineHeight,
        // 3 line-height units + extra space for gaps
        minHeight: `3.5lh`,
        maxHeight: `3.5lh`,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.25,
      })}
    >
      {linesToDisplay.map((line, index) => (
        <Box
          key={`thinking-process-${line}`}
          sx={{
            flexShrink: 0,
            animation: index > 0 ? `${thinkingLineReveal} 0.5s ease-out` : undefined,
          }}
        >
          <Typography variant="body2" component="p" sx={theme => thinkingRowTypographySx(theme)}>
            {line}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}
